import { styled } from '@/ui';

import type { FC } from 'react';

export interface SeriesCoachConflictNoteProps {
  className?: string;
  /** RFC3339 starts of the conflicting sessions, ascending. */
  conflictingOccurrenceStarts: string[];
  /** `series` while booking, `update` while rescheduling a whole series. */
  scope: 'series' | 'update';
  timeZone: string;
}

const formatConflictDate = (startAt: string, timeZone: string) =>
  new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'short',
    timeZone,
  }).format(new Date(startAt));

const NoteBox = styled('div')(({ theme }) => ({
  '& p': {
    margin: 0,
  },
  backgroundColor: theme.palette.info.tonal,
  border: `1px solid ${theme.palette.info.main}`,
  borderRadius: '7px',
  color: theme.palette.text.primary,
  fontSize: '14px',
  letterSpacing: '0.3px',
  lineHeight: '19px',
  padding: theme.spacing(1.5, 2),
}));

const NoteHeading = styled('p')(({ theme }) => ({
  color: theme.palette.info.main,
  fontWeight: 700,
}));

/** MEN-5428: tells a member which series sessions conflict with the coach. */
export const SeriesCoachConflictNote: FC<SeriesCoachConflictNoteProps> = ({
  className,
  conflictingOccurrenceStarts,
  scope,
  timeZone,
}) => {
  const count = conflictingOccurrenceStarts.length;
  if (count === 0) return null;

  return (
    <NoteBox aria-label="Coach conflicts" className={className} role="note">
      <NoteHeading>
        {`Note: ${count} coach ${count === 1 ? 'conflict' : 'conflicts'} in this ${scope}`}
      </NoteHeading>
      <p>Ask your coach about rescheduling</p>
      <p>
        {conflictingOccurrenceStarts
          .map((start) => formatConflictDate(start, timeZone))
          .join(' • ')}
      </p>
    </NoteBox>
  );
};

export default SeriesCoachConflictNote;
